import type { FormatOptions } from 'date-fns/format';

import { format, isValid, parse, parseISO } from 'date-fns';

import { assert } from '#errors.js';

/**
 * A polymorphic type representing a date, which can be a `Date` object, a string,
 * a number (timestamp), or a Firestore-like timestamp object.
 */
export type PolyDate = Date | string | number | { seconds: number; nanoseconds?: number };

/**
 * A robust function to convert a `PolyDate` into a standard JavaScript `Date` object.
 *
 * @param timestamp - The date representation to convert.
 * @returns The converted `Date` object, or `undefined` if the input was undefined.
 * @throws {Error} If the input is an invalid or unrecognized date format.
 */
export function ts(timestamp: PolyDate): Date;
export function ts(timestamp: undefined): undefined;
export function ts(timestamp: PolyDate | undefined): Date | undefined {
  if (!timestamp) return;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'number') return new Date(timestamp);
  if (typeof timestamp === 'object' && 'seconds' in timestamp) {
    return new Date(timestamp.seconds * 1000);
  }
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    if (!Number.isNaN(date.getTime())) return date;
  }
  throw new Error(`Invalid timestamp: ${timestamp}`);
}

/**
 * Formats a date into a string using `date-fns`.
 *
 * @param date - The date to format.
 * @param [formatStr='yyyy-MM-dd HH:mm:ss'] - The format string.
 * @param [options] - Additional options for `date-fns`.
 * @returns The formatted date string, or '—' if an error occurs.
 */
export function formatTime(
  date: string | number | Date,
  formatStr = 'yyyy-MM-dd HH:mm:ss',
  options?: FormatOptions,
): string {
  if (!date) return '—';
  try {
    return format(date, formatStr, options);
  } catch (error) {
    console.error(`Error formatting date: ${date}`, error, date);
    return '—';
  }
}

export function dateNow(formatStr = 'MMM d, yyyy'): string {
  return formatTime(new Date(), formatStr);
}

export function timeNow(formatStr = 'yyyy-MM-dd HH:mm:ss'): string {
  return formatTime(new Date(), formatStr);
}
const DIGIT_ONLY_REGEX = /^[+-]?\d+$/;
const EXPLICIT_TIMEZONE_REGEX = /Z|GMT|UTC|[+-]\d{2}:?\d{2}/i;
const HAS_TIME_REGEX = /T\d{2}:\d{2}|\d{1,2}:\d{2}|\b(?:AM|PM)\b/i;
const TIMEZONE_TOKEN_REGEX = /[xXzZoO]/;
const ISO_START_REGEX = /^\d{4}-\d{2}-\d{2}/;
const ISO_EXPLICIT_OFFSET_REGEX = /(?:Z|[+-]\d{2}:?\d{2})$/i;

const DATE_ONLY_INPUT_FORMATS = [
  'yyyy-MM-dd',
  'MM/dd/yyyy',
  'MM-dd-yyyy',
  'dd/MM/yyyy',
  'dd-MM-yyyy',
  'yyyy/MM/dd',
  'yyyyMMdd',
  'MMM d, yyyy',
  'MMMM d, yyyy',
  'd MMM yyyy',
  'd MMMM yyyy',
  'EEE, dd MMM yyyy',
  'EEE MMM dd yyyy',
] as const;

const DATE_TIME_INPUT_FORMATS = [
  "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  "yyyy-MM-dd'T'HH:mm:ssxxx",
  "yyyy-MM-dd'T'HH:mm:ss.SSSX",
  "yyyy-MM-dd'T'HH:mm:ssX",
  "yyyy-MM-dd'T'HH:mm:ss",
  "yyyy-MM-dd'T'HH:mm",
  'yyyy-MM-dd HH:mm:ss',
  'yyyy-MM-dd HH:mm',
  'yyyy/MM/dd HH:mm:ss',
  'yyyy/MM/dd HH:mm',
  'yyyyMMddHHmmss',
  'yyyyMMddHH',
  'yyyyMMddHHmm',
  'MM/dd/yyyy HH:mm:ss',
  'MM/dd/yyyy HH:mm',
  'MM/dd/yyyy hh:mm a',
  'MM-dd-yyyy HH:mm:ss',
  'MM-dd-yyyy HH:mm',
  'dd/MM/yyyy HH:mm:ss',
  'dd/MM/yyyy HH:mm',
  'dd-MM-yyyy HH:mm:ss',
  'dd-MM-yyyy HH:mm',
  'MMM d, yyyy HH:mm:ss',
  'MMM d, yyyy HH:mm',
  'd MMM yyyy HH:mm:ss',
  'd MMM yyyy HH:mm',
  'MMMM d, yyyy HH:mm:ss',
  'MMMM d, yyyy HH:mm',
  'EEE, dd MMM yyyy HH:mm:ss xxx',
  'EEE, dd MMM yyyy HH:mm:ss xx',
  'EEE MMM dd yyyy HH:mm:ss xxx',
  'EEE MMM dd yyyy HH:mm:ss xx',
  'EEE MMM dd HH:mm:ss xxx yyyy',
  'EEE MMM dd HH:mm:ss xx yyyy',
] as const;

const NUMERIC_COMPACT_PATTERNS = [
  { length: 8, pattern: 'yyyyMMdd' },
  { length: 10, pattern: 'yyyyMMddHH' },
  { length: 12, pattern: 'yyyyMMddHHmm' },
  { length: 14, pattern: 'yyyyMMddHHmmss' },
] as const;

export type NormalizedDate = [value: Date, preserveLocal: boolean];

interface NormalizationContext {
  raw: string;
  trimmed: string;
  isDigitsOnly: boolean;
  numericLength: number;
  explicitTimezone: boolean;
  hasTimeComponent: boolean;
}

type NormalizationStrategy = (ctx: NormalizationContext) => NormalizedDate | undefined;

const normalizationStrategy = (strategy: NormalizationStrategy) => strategy;

const parseNumericCompact = normalizationStrategy((ctx) => {
  if (!ctx.isDigitsOnly) return;
  const signless = ctx.trimmed.replace(/^[+-]/, '');
  for (const { length, pattern } of NUMERIC_COMPACT_PATTERNS) {
    if (ctx.numericLength === length) {
      const parsed = parse(signless, pattern, new Date());
      if (isValid(parsed)) {
        return [parsed, true];
      }
    }
  }
  return;
});
const parseEpochNumeric = normalizationStrategy((ctx) => {
  if (!ctx.isDigitsOnly) return;
  const numeric = Number(ctx.trimmed);
  if (Number.isNaN(numeric)) return;

  if (ctx.numericLength === 10 && Math.abs(numeric) >= 1_000_000_000) {
    const timestamp = new Date(numeric * 1000);
    if (isValid(timestamp)) {
      return [timestamp, false];
    }
  }

  if (ctx.numericLength > 10) {
    const timestamp = new Date(numeric);
    if (isValid(timestamp)) {
      return [timestamp, false];
    }
  }

  return;
});
const parseIsoLike = normalizationStrategy((ctx) => {
  if (!ISO_START_REGEX.test(ctx.trimmed)) return;
  try {
    const parsed = parseISO(ctx.trimmed);
    if (!isValid(parsed)) return;
    const hasExplicitOffset = ISO_EXPLICIT_OFFSET_REGEX.test(ctx.trimmed);
    return [parsed, !(ctx.explicitTimezone || hasExplicitOffset)];
  } catch {
    return;
  }
});
const parseByPatterns = normalizationStrategy((ctx) => {
  const patterns = ctx.hasTimeComponent ? DATE_TIME_INPUT_FORMATS : DATE_ONLY_INPUT_FORMATS;
  for (const pattern of patterns) {
    const parsed = parse(ctx.trimmed, pattern, new Date());
    if (isValid(parsed)) {
      const preserveLocal = !(ctx.explicitTimezone || TIMEZONE_TOKEN_REGEX.test(pattern));
      return [parsed, preserveLocal];
    }
  }
  return;
});
const parseNative = normalizationStrategy((ctx) => {
  const parsed = new Date(ctx.trimmed);
  if (isValid(parsed)) {
    return [parsed, !ctx.explicitTimezone];
  }
  return;
});

const NORMALIZATION_STRATEGIES: readonly NormalizationStrategy[] = [
  parseNumericCompact,
  parseEpochNumeric,
  parseIsoLike,
  parseByPatterns,
  parseNative,
];

export function tryReadDate(input: string | number | Date): NormalizedDate {
  if (input instanceof Date) {
    assert(isValid(input), 'Invalid date value: Date is invalid');
    return [new Date(input), false];
  }
  const raw = typeof input === 'string' ? input : String(input);
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    throw new Error('Invalid date value: received empty string');
  }

  const isDigitsOnly = DIGIT_ONLY_REGEX.test(trimmed);
  const signlessLength = isDigitsOnly ? trimmed.replace(/^[+-]/, '').length : 0;
  const explicitTimezone = EXPLICIT_TIMEZONE_REGEX.test(trimmed);
  const hasTimeComponent = HAS_TIME_REGEX.test(trimmed) || (isDigitsOnly && signlessLength > 8);

  const context: NormalizationContext = {
    raw,
    trimmed,
    isDigitsOnly,
    numericLength: signlessLength,
    explicitTimezone,
    hasTimeComponent,
  };

  for (const strategy of NORMALIZATION_STRATEGIES) {
    const result = strategy(context);
    if (result) {
      return result;
    }
  }

  throw new Error(`Invalid date value: unable to parse "${input}"`);
}

const DATE_ONLY_STRING_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalizes a date input to a date-only string (YYYY-MM-DD) for storage.
 * Use for calendar dates (e.g. trip start/end) so that timezone does not shift the day.
 * - If the input is already YYYY-MM-DD, returns it as-is.
 * - Otherwise parses the value and formats the UTC date as YYYY-MM-DD.
 */
export function toDateOnlyString(input: string | number | Date): string {
  if (typeof input === 'string' && DATE_ONLY_STRING_REGEX.test(input.trim())) {
    return input.trim();
  }
  const [parsed] = tryReadDate(input);
  const y = parsed.getUTCFullYear();
  const m = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const d = String(parsed.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
